import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSidecarMode } from '../../context/SidecarModeContext';
import '../../styles/SidecarRouteGuard.css';

// Routes blocked in sidecar mode
const BLOCKED_ROUTES = [
  '/chemistry/files',
  '/chemistry/file',
  '/chemistry/projects',
  '/import',
  '/admin',
  '/omics',
];

interface SidecarRouteGuardProps {
  children: React.ReactNode;
}

export const SidecarRouteGuard: React.FC<SidecarRouteGuardProps> = ({ children }) => {
  const { sidecarMode, toggleSidecarMode } = useSidecarMode();
  const location = useLocation();

  if (!sidecarMode) {
    return <>{children}</>;
  }

  // Legacy safety:
  // "/external-intelligence" no longer has its own page and now redirects back to
  // "/chemistry". Redirecting there from sidecar mode creates a blank navigation
  // loop for users with persisted sidecar state, so we fail open on the main
  // chemistry landing routes instead of bouncing away.
  if (location.pathname === '/' || location.pathname === '/chemistry') {
    return <>{children}</>;
  }

  // Check if current route is blocked
  const isBlocked = BLOCKED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  if (isBlocked) {
    return <SidecarBlockedPage onExitSidecar={() => toggleSidecarMode()} />;
  }

  return <>{children}</>;
};

interface SidecarBlockedPageProps {
  onExitSidecar: () => void;
}

const SidecarBlockedPage: React.FC<SidecarBlockedPageProps> = ({ onExitSidecar }) => {
  return (
    <div className="sidecar-blocked">
      <div className="sidecar-blocked__content">
        <div className="sidecar-blocked__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h2 className="sidecar-blocked__title">Not Available in Discovery Mode</h2>
        <p className="sidecar-blocked__description">
          This feature is part of the full platform and is hidden in Discovery Mode.
        </p>
        <button className="sidecar-blocked__button" onClick={onExitSidecar}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Exit Discovery Mode
        </button>
      </div>
    </div>
  );
};

export default SidecarRouteGuard;
